import { Button } from "@/components/ui/Button";
import { CitationSidebar } from "@/features/chat/components/CitationSidebar";
import { MessageList } from "@/features/chat/components/MessageList";
import {
  PromptInput,
  type PromptContext,
  type PromptInputHandle,
} from "@/features/chat/components/PromptInput";
import {
  cellChips,
  cellContextText,
  cellKey,
  type SelectedGridCell,
} from "@/features/data-curation/cellSelection";
import {
  ChatSessionProvider,
  useChatSession,
} from "@/features/chat/context/ChatSessionProvider";
import {
  CitationProvider,
  useCitations,
} from "@/features/chat/context/CitationProvider";
import { AGENT_CONFIG_PATH } from "@/features/chat/types";
import {
  useRefreshExtractionDataset,
  useStopExtractionCollection,
} from "@/features/data-curation/hooks/useCuration";
import type {
  ExtractionCollectionTurn,
  ExtractionDataset,
} from "@/features/data-curation/types";
import { Check, PanelRightClose, Plus, RefreshCw, Square } from "lucide-react";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from "react";
import { toast } from "sonner";
import { DatasetModePicker } from "./DatasetModePicker";

/**
 * A request from the page: `text` goes into the chat box (and is sent at once
 * when `send` is set and the agent is free); `cells` attach to the next message.
 */
export interface ChatRequest {
  id: number;
  text?: string;
  send?: boolean;
  cells?: SelectedGridCell[];
}

/** The chat box, for the buttons of the page that message the agent themselves. */
export interface ChatComposerHandle {
  /** The message that the user typed and did not send. Empty when there is none. */
  unsentMessage: () => string;
  /** Empties the chat box after its message went to the agent with another message. */
  clear: () => void;
}

type CurationChatPanelProps =
  | {
      /** The selected curation; its one chat, saved like any Paperclip chat. */
      dataset: ExtractionDataset;
      pendingSessionId?: undefined;
      /** Whether the curation's agent runs, in this tab or on the server. */
      agentWorking: boolean;
      request?: ChatRequest | null;
      onRequestHandled?: (id: number) => void;
      composerRef?: Ref<ChatComposerHandle>;
      onHide: () => void;
    }
  | {
      dataset?: undefined;
      /** A new-curation chat whose agent is still setting the curation up. */
      pendingSessionId: string;
      agentWorking?: undefined;
      request?: undefined;
      onRequestHandled?: undefined;
      composerRef?: undefined;
      onHide: () => void;
    };

export const CurationChatPanel = ({
  dataset,
  pendingSessionId,
  agentWorking,
  request,
  onRequestHandled,
  composerRef,
  onHide,
}: CurationChatPanelProps) => {
  const refreshDataset = useRefreshExtractionDataset();
  const chatKey = dataset ? dataset.id : `pending:${pendingSessionId}`;
  // A curation without a chat gets one from its first message.
  const [created, setCreated] = useState<{
    datasetId: string;
    sessionId: string;
  } | null>(null);
  const createdSessionId =
    dataset && created?.datasetId === dataset.id ? created.sessionId : undefined;
  const sessionId = dataset
    ? (dataset.chat_session_id ?? createdSessionId)
    : pendingSessionId;

  const datasetId = dataset?.id;
  const handleSessionCreated = useCallback(
    (newSessionId: string) => {
      if (!datasetId) return;
      setCreated({ datasetId, sessionId: newSessionId });
      refreshDataset(datasetId);
    },
    [datasetId, refreshDataset],
  );
  // A stop also ends the turns that would continue the collection after this one.
  const stopCollection = useStopExtractionCollection(datasetId ?? null);
  const { mutate: stopTurns } = stopCollection;
  const handleStop = useCallback(() => {
    if (datasetId) stopTurns();
  }, [datasetId, stopTurns]);

  return (
    <ChatSessionProvider
      key={chatKey}
      sessionId={sessionId}
      agentConfigPath={AGENT_CONFIG_PATH}
      onSessionCreated={handleSessionCreated}
      extractionId={dataset && !dataset.chat_session_id ? dataset.id : undefined}
      dataCurationChat
      onStop={datasetId ? handleStop : undefined}
    >
      <CitationProvider>
        <CurationChatBody
          key={chatKey}
          dataset={dataset ?? null}
          agentWorking={agentWorking}
          request={request ?? null}
          onRequestHandled={onRequestHandled}
          composerRef={composerRef}
          stoppingTurns={stopCollection.isPending}
          onStopTurns={handleStop}
          onHide={onHide}
        />
      </CitationProvider>
    </ChatSessionProvider>
  );
};

/** A turn that Paperclip started to continue the collection, with a Stop button. */
const CollectionTurnControl = ({
  turn,
  stopping,
  onStop,
}: {
  turn: ExtractionCollectionTurn;
  stopping: boolean;
  onStop: () => void;
}) => (
  <span className="flex shrink-0 items-center gap-1.5">
    <span
      className="hidden items-center gap-1 text-xs tabular-nums text-zinc-500 sm:inline-flex"
      aria-live="polite"
    >
      <RefreshCw className="h-3.5 w-3.5" aria-hidden />
      {turn.status === "stopping"
        ? "Stopping"
        : `Turn ${turn.turn} of up to ${turn.max_turns}`}
    </span>
    <button
      type="button"
      onClick={onStop}
      disabled={stopping || turn.status === "stopping"}
      aria-label={`Stop the collection turns (turn ${turn.turn} of up to ${turn.max_turns})`}
      className="inline-flex h-7 items-center gap-1 rounded-[3px] border border-zinc-300 px-2 text-xs font-medium text-zinc-700 hover:bg-zinc-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 disabled:opacity-50"
    >
      <Square className="h-3 w-3" aria-hidden />
      Stop
    </button>
  </span>
);

type AgentState = "working" | "done" | null;

const AgentStatePill = ({ state }: { state: AgentState }) => (
  <span role="status" aria-live="polite" className="shrink-0">
    {state === "working" && (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
        <span
          className="h-2 w-2 rounded-full bg-blue-600 motion-safe:animate-pulse"
          aria-hidden
        />
        Agent working
      </span>
    )}
    {state === "done" && (
      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
        <Check className="h-3.5 w-3.5" aria-hidden />
        Agent is done. Your turn.
      </span>
    )}
  </span>
);

const CurationChatHeader = ({
  subtitle,
  agentState = null,
  turnControl = null,
  onHide,
}: {
  subtitle: string;
  agentState?: AgentState;
  turnControl?: ReactNode;
  onHide: () => void;
}) => (
  <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-200 px-3 py-2.5">
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-zinc-900">Chat</p>
      <p className="truncate text-xs text-zinc-500">{subtitle}</p>
    </div>
    {turnControl}
    <AgentStatePill state={agentState} />
    <button
      type="button"
      onClick={onHide}
      aria-label="Hide chat"
      title="Hide chat"
      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[3px] text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900"
    >
      <PanelRightClose className="h-4 w-4" aria-hidden />
    </button>
  </div>
);

/** Shown while no curation is selected (or the selected one is loading). */
export const CurationChatPlaceholder = ({
  onStart,
  onHide,
}: {
  onStart: () => void;
  onHide: () => void;
}) => (
  <div className="flex h-full min-h-0 flex-col bg-white">
    <CurationChatHeader subtitle="No dataset selected" onHide={onHide} />
    <div className="px-4 py-6 text-sm leading-6 text-zinc-600">
      <p>
        Each dataset has its own chat. Select a dataset to continue its
        chat, or start a new one.
      </p>
      <Button size="sm" className="mt-4" onClick={onStart}>
        <Plus className="h-4 w-4" aria-hidden />
        Start a dataset
      </Button>
    </div>
  </div>
);

const REQUEST_RETRY_MS = 100;
const REQUEST_TRIES = 50;

const CurationChatBody = ({
  dataset,
  agentWorking,
  request,
  onRequestHandled,
  composerRef,
  stoppingTurns = false,
  onStopTurns,
  onHide,
}: {
  dataset: ExtractionDataset | null;
  agentWorking?: boolean;
  request: ChatRequest | null;
  onRequestHandled?: (id: number) => void;
  composerRef?: Ref<ChatComposerHandle>;
  stoppingTurns?: boolean;
  onStopTurns?: () => void;
  onHide: () => void;
}) => {
  const { selectedCitation, closeCitation } = useCitations();
  const { isStreaming, isLoading } = useChatSession();
  const promptRef = useRef<PromptInputHandle>(null);
  useImperativeHandle(
    composerRef,
    () => ({
      unsentMessage: () => promptRef.current?.unsentText() ?? "",
      clear: () => promptRef.current?.clear(),
    }),
    [],
  );
  const busy = Boolean(agentWorking) || isStreaming || isLoading;
  const [attachedCells, setAttachedCells] = useState<SelectedGridCell[]>([]);
  const promptContext = useMemo<PromptContext>(() => {
    const chips = cellChips(attachedCells);
    return {
      chips,
      text: attachedCells.length > 0 ? cellContextText(attachedCells) : "",
      onRemove: (key) => {
        const removed = new Set(
          chips
            .find((chip) => chip.key === key)
            ?.cells.map((cell) => cellKey(cell.rowId, cell.columnId)) ?? [],
        );
        setAttachedCells((current) =>
          current.filter((cell) => !removed.has(cellKey(cell.rowId, cell.columnId))),
        );
      },
      onSent: () => setAttachedCells([]),
    };
  }, [attachedCells]);
  const busyRef = useRef(busy);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  // The prompt box mounts with the panel, so wait until its editor is ready.
  useEffect(() => {
    if (!request) return;
    if (selectedCitation) {
      closeCitation();
      return;
    }
    const { cells, text } = request;
    if (cells) {
      setAttachedCells((current) => {
        const keys = new Set(current.map((cell) => cellKey(cell.rowId, cell.columnId)));
        return [
          ...current,
          ...cells.filter((cell) => !keys.has(cellKey(cell.rowId, cell.columnId))),
        ];
      });
    }
    let tries = 0;
    let timer: number | undefined;
    const apply = () => {
      const prompt = promptRef.current;
      if (text == null) {
        if (!prompt) {
          tries += 1;
          if (tries < REQUEST_TRIES) timer = window.setTimeout(apply, REQUEST_RETRY_MS);
          return;
        }
        prompt.focus();
        onRequestHandled?.(request.id);
        return;
      }
      if (!prompt?.setText(text)) {
        tries += 1;
        if (tries < REQUEST_TRIES) timer = window.setTimeout(apply, REQUEST_RETRY_MS);
        return;
      }
      if (request.send && !busyRef.current) {
        prompt.submit();
      } else {
        prompt.focus();
        if (request.send) {
          toast.info("The agent is working. Your message is in the chat box. Send it when the agent is done.");
        }
      }
      onRequestHandled?.(request.id);
    };
    apply();
    return () => window.clearTimeout(timer);
  }, [request, selectedCitation, closeCitation, onRequestHandled]);
  // "Done" shows only after this view saw the agent work, not for an old chat.
  const [sawWork, setSawWork] = useState(false);
  useEffect(() => {
    if (agentWorking) setSawWork(true);
  }, [agentWorking]);
  const agentState: AgentState = agentWorking ? "working" : sawWork ? "done" : null;

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-white">
      {agentWorking && (
        <div
          className="pointer-events-none absolute inset-0 z-10 ring-2 ring-inset ring-blue-500 motion-safe:animate-pulse"
          aria-hidden
        />
      )}
      <CurationChatHeader
        subtitle={dataset ? dataset.name : "New dataset"}
        agentState={agentState}
        turnControl={
          dataset?.collection_turn && onStopTurns ? (
            <CollectionTurnControl
              turn={dataset.collection_turn}
              stopping={stoppingTurns}
              onStop={onStopTurns}
            />
          ) : null
        }
        onHide={onHide}
      />
      {selectedCitation ? (
        <div className="min-h-0 flex-1 overflow-hidden">
          <CitationSidebar onClose={closeCitation} />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <MessageList
            compact
            emptyState={<CurationChatEmptyState dataset={dataset} />}
          />
          <PromptInput
            ref={promptRef}
            compact
            context={promptContext}
            placeholder={
              dataset
                ? "Ask the agent about this dataset..."
                : "Describe the dataset you want..."
            }
            actions={<DatasetModePicker />}
          />
        </div>
      )}
    </div>
  );
};

const CurationChatEmptyState = ({
  dataset,
}: {
  dataset: ExtractionDataset | null;
}) => (
  <div className="px-4 py-6 text-sm leading-6 text-zinc-600">
    {dataset ? (
      <>
        <p className="font-medium text-zinc-900">
          Chat about “{dataset.name}”
        </p>
        <p className="mt-2">
          Ask the agent to collect rows, fix rejected values, or save a
          checkpoint. It checks the dataset’s current state first, so it also
          sees changes made by other agents, such as Claude Code.
        </p>
      </>
    ) : (
      <p>Loading this chat…</p>
    )}
  </div>
);
